import { Title, Box, Group, Stack, Radio, Text, Grid, Progress } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { PageWithBreadcrumbs } from "../../components/layout/PageWithBreadcrumbs";
import { ContainerWhite } from "../../components/core/ContainerWhite/ContainerWhite";
import {
  AfaCombobox,
  AfaFieldDisplay,
  AfaDropzone,
  AfaTextarea,
  AfaCheckbox,
  AfaAnchor,
  AfaButton,
  AfaModal,
  AfaAlert,
  AfaTextInput,
  AfaSelect,
  AfaDatePickerInput,
  AfaPageLoader,
} from "../../components/ui";
import { useEffect, useState, useRef } from "react";
import DestinatarisEditable, {
  DestinatarisEditableRef,
} from "./../Trameses/destinataris";
import { convertToBase64, base64ToFile } from "../../utils/Utils";
import { tramesesGroupedType } from "../../interface/trameses";
import * as XLSX from "xlsx";
import mammoth from "mammoth";


const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB — seguro para Consumption Plan
const MAX_CHUNK_RETRIES = 3;
const MAX_SESSION_RETRIES = 2; // reintentos desde 0 si se aborta la sesión

async function uploadFileInChunks(file: any, itemId: any, listName: string, apiService: any) {
  let lastSessionError: any;

  for (let sessionAttempt = 1; sessionAttempt <= MAX_SESSION_RETRIES; sessionAttempt++) {
    try {
      // 1. Crear sesión
      const { sessionId } = await apiService.InitUploadSession({
        itemId,
        listName,
        fileName: "00_" + file.name,
        fileSize: file.size,
      });

      // 2. Enviar chunks binarios amb reintentos
      for (let start = 0; start < file.size; start += CHUNK_SIZE) {
        const end   = Math.min(start + CHUNK_SIZE - 1, file.size - 1);
        const chunk = file.slice(start, end + 1);

        let lastError: any;
        for (let attempt = 1; attempt <= MAX_CHUNK_RETRIES; attempt++) {
          try {
            await apiService.UploadChunk(chunk, {
              'x-session-id':  sessionId,
              'x-range-start': start,
              'x-range-end':   end,
              'x-file-size':   file.size,
            });
            lastError = null;
            break;
          } catch (error: any) {
            lastError = error;
            const httpStatus = error?.response?.status;
            // 5xx errors may leave the server session in a corrupted state — abort immediately,
            // do not retry (a retry of the same chunk could produce a corrupt file).
            if (httpStatus && httpStatus >= 500) break;
            if (attempt < MAX_CHUNK_RETRIES) {
              await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            }
          }
        }

        if (lastError) {
          try { await apiService.AbortUploadSession(sessionId); } catch (_) {}
          throw new Error(
            `Error pujant el fitxer "${file.name}" (byte ${start}–${end}): ${lastError.message}`,
          );
        }
      }

      return; // subida completada con éxito
    } catch (error: any) {
      lastSessionError = error;
      if (sessionAttempt < MAX_SESSION_RETRIES) {
        // Esperar antes de reintentar desde 0 con una nueva sesión
        await new Promise((resolve) => setTimeout(resolve, sessionAttempt * 2000));
      }
    }
  }

  throw lastSessionError;
}

// Página para iniciar un nuevo trámite
export function TramitsIniciarNou(props: any) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const destinatarisRef = useRef<DestinatarisEditableRef>(null);
  const location = useLocation();

  const [tramitId, setTramitId] = useState<string | undefined>(id);
  // Tramits Data
  const [tramitData, setTramitData] = useState<any>(null);
  const [initialProcesId, setInitialProcesId] = useState<string | null>(null);

  const [tramitsFilialData, setTramitsFilialData] = useState<
    { value: string; label: string }[]
  >([]);
  const [tramitsFilialValue, setTramitsFilialValue] = useState<string>("");

  const [tramitsTipusProcesData, setTramitsTipusProcesData] = useState<
    {
      value: string;
      label: string;
      periodicity: string;
      startDate: string;
      initialMonth: string;
    }[]
  >([]);
  const [tramitsTipusProcesValue, setTramitsTipusProcesValue] =
    useState<string>("");

  const [allTramitsProcesData, setAllTramitsProcesData] = useState<any[]>([]);
  const [tramitsProcesData, setTramitsProcesData] = useState<any[]>([]);
  const [tramitsProcesValue, setTramitsProcesValue] = useState<string>("");
  const [unsavedModalOpened, setUnsavedModalOpened] = useState(false);
  const [unsavedEmail, setUnsavedEmail] = useState("");
  const [guidValue, setGuidValue] = useState<string>("");

  const [tramitsProcesAnteriorData, setTramitsProcesAnteriorData] = useState<
    { value: string; label: string }[]
  >([]);
  const [tramitsProcesAnteriorValue, setTramitsProcesAnteriorValue] =
    useState<any>(null);

  const [groups, setGroups] = useState<tramesesGroupedType[]>([]);

  // Edit Mode
  const [editar, setEditar] = useState<boolean>(false);
  const [titleInit, setTitleInit] = useState<string>("Iniciar Nou Tràmit");

  const [tipoReferencia, setTipoReferencia] = useState<string>("");
  const [uploadedFiles, setUploadedFiles] = useState<
    Array<{
      file: File;
      name: string;
      type: "pdf" | "doc" | "xls" | "ppt" | "png" | "jpg" | "other";
    }>
  >([]);
  const [oldFiles, setOldFiles] = useState<
    Array<{
      file: File;
      name: string;
      type: "pdf" | "doc" | "xls" | "ppt" | "png" | "jpg" | "other";
    }>
  >([]);

  const [deletedFiles, setDeletedFiles] = useState<string[]>([]);

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [cancelModalOpened, setCancelModalOpened] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );
  const [infoInteres, setInfoInteres] = useState([] as any[]);

  // Form Data
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [getComentarisValue, setComentarisValue] = useState("");
  const [otherReceivers, setOtherReceivers] = useState<string[]>([]);

  // Loading States
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [loadingFilial, setLoadingFilial] = useState<boolean>(false);
  const [loadingPage, setLoadingPage] = useState<boolean>(true);
  const [loadResponsePDF, setLoadResponse] = useState<boolean>(false);
  const [pdf, setPDF] = useState<string>("");
  const [codi, setCodi] = useState<string>("");

  const normalizePdfBase64 = (pdf: any): string => {
    if (!pdf) return "";
    if (typeof pdf === "string") {
      return pdf;
    }
    if (Array.isArray(pdf)) {
      return btoa(
        pdf.reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );
    }

    return "";
  };

  // Enable
  const [tipusProcesEnable, setTipusProcesEnable] = useState<boolean>(false);
  const [procesEnable, setProcesEnable] = useState<boolean>(false);
  const [procesAnteriorEnable, setProcesAnteriorEnable] =
    useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);
  const [checkTerms, setCheckTerms] = useState(false);

  const handleBreadcrumbNavigation = async (to: string): Promise<boolean> => {
    // Almacenar la URL pendiente y mostrar el modal de cancelación
    setPendingNavigation(to);
    setCancelModalOpened(true);
    return false; // Prevenir la navegación inmediata
  };

  const getFileExtension = (filename: string): string | null => {
    const cleanName = filename.trim();

    const lastDotIndex = cleanName.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === cleanName.length - 1) {
      return null;
    }

    return cleanName.substring(lastDotIndex + 1).toLowerCase();
  };

  type ValidationResult = { valid: true } | { valid: false; message: string };

  const validateExcelA1 = async (file: File): Promise<ValidationResult> => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    const [sheetName] = workbook.SheetNames;
    if (!sheetName)
      return { valid: false, message: "Error: hoja no encontrada" };

    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) return { valid: false, message: "Error: hoja inválida" };

    const data = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      raw: true,
      blankrows: true,
    });

    const cellA1 = data?.[0]?.[0];

    if (
      cellA1 === undefined ||
      cellA1 === null ||
      String(cellA1).trim() === ""
    ) {
      return {
        valid: false,
        message: "Si us plau, adjunteu la plantilla correcta",
      };
    }

    if (String(cellA1) !== "1618") {
      return {
        valid: false,
        message: "Si us plau, adjunteu la plantilla correcta",
      };
    }

    return { valid: true };
  };

  const getExcelCode = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const [sheetName] = workbook.SheetNames;
    if (!sheetName) return "";
    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) return "";
    const data = XLSX.utils.sheet_to_json<any[]>(worksheet, {
      header: 1,
      raw: true,
      blankrows: true,
    });
    return String(data?.[2]?.[0] ?? "");
  };

  const validateExcelHasData = async (file: File): Promise<boolean> => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return false;

    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return false;

    const data = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });

    return data.flat().some((cell) => String(cell ?? "").trim() !== "");
  };

  const validateWordHasText = async (file: File): Promise<boolean> => {
    const buffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: buffer });
    const text = result.value?.trim();

    return Boolean(text && text.length > 0);
  };

  useEffect(() => {
    if (location.state?.fromUploadDocs) {
      setLoadResponse(true);
      setCodi(location.state.codi ?? "");
      setTramitId(id);

      const normalizedPdf = normalizePdfBase64(location.state.pdf);
      setPDF(normalizedPdf);
    }
  }, []);

  useEffect(() => {
    if (editar && tramitsProcesValue && allTramitsProcesData.length > 0) {
      handleTramitsProcesChange(tramitsProcesValue);
    }
  }, [editar, tramitsProcesValue, allTramitsProcesData]);

  useEffect(() => {
    if (
      procesAnteriorEnable &&
      tramitsProcesAnteriorData.length > 0 &&
      guidValue
    ) {
      const selected = tramitsProcesAnteriorData.find(
        (p) => p.label === guidValue || String(p.value) === String(guidValue),
      );
      if (selected) {
        setTramitsProcesAnteriorValue(selected.value);
      }
    }
  }, [procesAnteriorEnable, tramitsProcesAnteriorData, guidValue]);

  // Initial Load Effect
  useEffect(() => {
    const init = async () => {
      try {
        props.apiService
          .GetRelevantInformation("tramits", props.entity.lookupId)
          .then((info: any) => {
            setInfoInteres(info);
          });

        const processID = searchParams.get("processID");
        const nextProcessType = searchParams.get("nextProcessType");

        if (
          location.state?.tramitType ||
          (processID !== "" && processID != null)
        ) {
          const tramitIdToLoad =
            location.state?.idType ??
            (processID !== "" && processID !== null ? processID : null);

          if (!tramitIdToLoad) return;

          const data = await props.apiService.GetTramitById(
            tramitIdToLoad,
            props.entity.lookupId,
          );
          setGuidValue(data.guid);
          const lookIdToLoad =
            location.state?.tramitType.lookupId ??
            (nextProcessType !== "" && nextProcessType !== null
              ? nextProcessType
              : null);
          data.tramitType.lookupId = lookIdToLoad;

          // Opcional Final -  Una tercera traerlo por data
          if (
            (nextProcessType !== "" && nextProcessType !== null) ||
            location.state?.tramitType.lookupId
          )
            data.tramitType.lookupValue = data.nextTramitType.lookupValue;

          setTramitData(data);
          setEditar(true);

          if (data.formJson) {
            const formJsonParsed = JSON.parse(data.formJson);
            setGroups(formJsonParsed);
          }

          if (data.filial.lookupId !== -1) {
            setTipoReferencia("filial");
            setTramitsFilialValue(String(data.filial.lookupId));

            const fieldsFilial = await props.apiService.GetFilialsFromEntity(
              props.entity.lookupId,
            );
            setTramitsFilialData(fieldsFilial);

            const fieldsTramesa = await props.apiService.GetTramitsTypes(
              "filialId",
              data.filial.lookupId,
            );

            setTramitsTipusProcesData(fieldsTramesa.tipus);
            setTipusProcesEnable(true);

            setTramitsProcesData(fieldsTramesa.proces);
            setAllTramitsProcesData(fieldsTramesa.proces);
            setLoadingTypes(false);

            setInitialProcesId(String(data.tramitType.lookupId));
          } else {
            setTipoReferencia("entitat");

            // Cargar tipos de tramesa para la entidad
            const fieldsTramesa = await props.apiService.GetTramitsTypes(
              "entityId",
              props.entity.lookupId,
            );
            setTramitsTipusProcesData(fieldsTramesa.tipus);
            setTipusProcesEnable(true);

            setAllTramitsProcesData(fieldsTramesa.proces);
            setTramitsProcesData(fieldsTramesa.proces);
            setTipusProcesEnable(true);
            setLoadingTypes(false);

            setInitialProcesId(String(data.tramitType.lookupId));
          }
        } else {
          if (id != null) {
            // Modo edición
            const data = await props.apiService.GetTramitById(
              id,
              props.entity.lookupId,
            );

            setTramitData(data);
            setEditar(true);
            setTitleInit(`Editar Tràmit: ${data.guid}`);

            if (data.formJson) {
              const formJsonParsed = JSON.parse(data.formJson);
              setGroups(formJsonParsed);
            }

            if (data.docs) {
              const files = data.docs.map((doc: any) => {
                const extension = getFileExtension(doc.title);
                let type:
                  | "pdf"
                  | "doc"
                  | "xls"
                  | "ppt"
                  | "png"
                  | "jpg"
                  | "other" = "other";

                if (extension === "pdf") type = "pdf";
                else if (extension === "doc" || extension === "docx")
                  type = "doc";
                else if (extension === "xls" || extension === "xlsx")
                  type = "xls";
                else if (extension === "ppt" || extension === "pptx")
                  type = "ppt";
                else if (extension === "png") type = "png";
                else if (extension === "jpg" || extension === "jpeg")
                  type = "jpg";

                const file = base64ToFile(doc.content, doc.title);

                return { file, name: doc.title, type };
              });
              setOldFiles(files);
              setUploadedFiles(files);
            }
            if (data.otherReceivers) setOtherReceivers(data.otherReceivers);
            if (data.comments && data.comments.length > 0)
              setComentarisValue(data.comments[0].commentText);

            if (data.filial.lookupId !== -1) {
              setTipoReferencia("filial");
              setTramitsFilialValue(String(data.filial.lookupId));

              const fieldsFilial = await props.apiService.GetFilialsFromEntity(
                props.entity.lookupId,
              );
              setTramitsFilialData(fieldsFilial);

              const fieldsTramesa = await props.apiService.GetTramitsTypes(
                "filialId",
                data.filial.lookupId,
              );

              setTramitsTipusProcesData(fieldsTramesa.tipus);
              setTipusProcesEnable(true);

              setTramitsProcesData(fieldsTramesa.proces);
              setAllTramitsProcesData(fieldsTramesa.proces);
              setLoadingTypes(false);

              setInitialProcesId(String(data.tramitType.lookupId));
            } else {
              setTipoReferencia("entitat");

              // Cargar tipos de tramesa para la entidad
              const fieldsTramesa = await props.apiService.GetTramitsTypes(
                "entityId",
                props.entity.lookupId,
              );
              setTramitsTipusProcesData(fieldsTramesa.tipus);
              setTipusProcesEnable(true);

              setAllTramitsProcesData(fieldsTramesa.proces);
              setTramitsProcesData(fieldsTramesa.proces);
              setTipusProcesEnable(true);
              setLoadingTypes(false);

              setInitialProcesId(String(data.tramitType.lookupId));
            }
          }
        }
        setLoadingPage(false);
      } catch (error) {
        console.error("Error carregant dades:", error);
        setLoadingPage(false);
        notifications.show({
          title: "Error",
          message: "Error al carregar les dades del tràmit",
          color: "red",
        });
      }
    };

    init();
  }, [id]);

  useEffect(() => {
    if (editar && initialProcesId && allTramitsProcesData.length > 0) {
      const selectedProces = allTramitsProcesData.find(
        (p: any) => String(p.value) === initialProcesId,
      );

      if (selectedProces) {
        setTramitsTipusProcesValue(selectedProces.processType);
        const filteredProces = allTramitsProcesData.filter(
          (p: any) => p.processType === selectedProces.processType,
        );
        setTramitsProcesData(filteredProces);
        setTramitsProcesValue(selectedProces.value);

        setProcesEnable(true);
      }
    }
  }, [editar, initialProcesId, allTramitsProcesData]);

  const handleClearSelect = async () => {
    setTipusProcesEnable(false);
    setProcesEnable(false);

    setTramitsTipusProcesData([]);
    setTramitsProcesData([]);
    setAllTramitsProcesData([]);

    setTramitsTipusProcesValue("");
    setTramitsProcesValue("");
  };

  const handleTramitsReferenceChange = async (value: string | null) => {
    if (editar) return;

    setTipoReferencia("");
    handleClearSelect();

    if (value === "filial") {
      setTipoReferencia(value);
      setLoadingFilial(true);

      try {
        const fieldsFilial = await props.apiService.GetFilialsFromEntity(
          props.entity.lookupId,
        );
        setTramitsFilialData(fieldsFilial);
        setTramitsFilialValue("");
      } catch (error) {
        console.error("Error fetching filial data:", error);
      } finally {
        setLoadingFilial(false);
      }
    } else if (value === "entitat") {
      setTipoReferencia(value);
      setLoadingTypes(true);
      try {
        const fieldsTipusProces = await props.apiService.GetTramitsTypes(
          "entityId",
          props.entity.lookupId,
        );

        setTramitsTipusProcesData(fieldsTipusProces.tipus);
        setTipusProcesEnable(true);

        setTramitsProcesData(fieldsTipusProces.proces);
        setAllTramitsProcesData(fieldsTipusProces.proces);
        setLoadingTypes(false);
      } catch (error) {
        console.error("Error fetching entity data:", error);
        setLoadingTypes(false);
      }
    }
  };

  const handleTramitsFilialChange = async (value: string | null) => {
    if (value) {
      setTramitsFilialValue(value);
      setLoadingTypes(true);
      try {
        const fieldsTipusProces = await props.apiService.GetTramitsTypes(
          "filialId",
          value,
        );

        setTramitsTipusProcesData(fieldsTipusProces.tipus);
        setTipusProcesEnable(true);

        setTramitsProcesData(fieldsTipusProces.proces);
        setAllTramitsProcesData(fieldsTipusProces.proces);
        setLoadingTypes(false);
      } catch (error) {
        console.error("Error fetching tràmit types:", error);
        setLoadingTypes(false);
      }
    }
  };

  const handleTramitsTipusProcesChange = async (value: string | null) => {
    if (value) {
      setTramitsTipusProcesValue(value);
      setTramitsProcesData(
        allTramitsProcesData.filter((item: any) => item.processType === value),
      );
      setProcesEnable(true);
    }
  };

  const handleTramitsProcesChange = async (value: string | null) => {
    if (value) {
      setTramitsProcesValue(value);

      try {
        var item = allTramitsProcesData.filter(
          (item: any) => item.value === value,
        );
        if (item.length > 0 && item[0].previousProcess !== -1) {
          setProcesAnteriorEnable(true);
          props.apiService
            .GetTramitByTypeId(item[0].previousProcess, props.entity.lookupId)
            .then((response: any) => {
              if (response.tramit.length > 0) {
                setTramitsProcesAnteriorData(
                  response.tramit.map((proc: any) => ({
                    value: proc.id,
                    label: proc.guid,
                  })),
                );
              } else {
                setTramitsProcesAnteriorData([]);
                notifications.show({
                  title: "Atenció",
                  message:
                    "No hi ha tràmits anteriors disponibles per aquest procés.",
                  color: "yellow",
                });
              }
            });
        } else setProcesAnteriorEnable(false);
      } catch (error) {
        console.error("Error fetching tràmit types:", error);
      }
    }
  };

  const handleTramitsProcesAnteriorChange = async (value: string | null) => {
    if (value) {
      setTramitsProcesAnteriorValue(parseInt(value));
    }
  };

  const handleEnviarTramesaNew = async (isDraft?: boolean) => {
    const errors: string[] = [];
    let isFormValid = true;

    const selectedRelatedTramit = tramitsProcesAnteriorData.find(
      (p) => String(p.value) === String(tramitsProcesAnteriorValue),
    );

    if (tipoReferencia === "") {
      errors.push("Tràmit referent es obligatori");
      isFormValid = false;
    }

    if (tramitsProcesValue === "") {
      errors.push("Procés es obligatori");
      isFormValid = false;
    }

    if (!checkTerms) {
      errors.push(
        "Si us plau accepteu els termes establerts al avís legal i la política de privacitat.",
      );
      isFormValid = false;
    }

    if (destinatarisRef.current?.hasUnsavedInput()) {
      setUnsavedEmail(destinatarisRef.current.getUnsavedInput());
      setUnsavedModalOpened(true);
      return;
    }

    if (!isFormValid) {
      notifications.show({
        title: <span style={{ fontSize: "15px" }}>Error de Validació</span>,
        message: (
          <div style={{ fontSize: "12px" }}>
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        ),
        color: "red",
      });
      return;
    }

    if (isFormValid) {
      try {
        setIsSubmitting(true);
        const selectedProces = tramitsProcesData.find(
          (p: any) => p.value === tramitsProcesValue,
        );

        const newFiles = uploadedFiles;
        // .filter(
        //   (file) => !oldFiles.some((old) => old.name === file.name)
        // );

        const docs = await Promise.all(
          newFiles.map(async (item) => {
            const base64 = await convertToBase64(item.file);
            if (selectedProces.label === "Ordres de pagament") {
              return {
                title: item.name,
                content: base64.split(",")[1],
              };
            } else {
              return {
                title: "00_" + item.name,
                content: base64.split(",")[1],
              };
            }
          }),
        );

        let excelTitle: string | undefined;

        if (selectedProces.label === "Ordres de pagament") {
          // Buscar un archivo .xlsx entre todos los archivos subidos
          const excelFile = uploadedFiles.find(
            (item) => item.type === "xls",
          )?.file;

          if (!excelFile) {
            notifications.show({
              title: "Error",
              message: "S'ha d'incloure el document Excel.",
              color: "red",
            });
            return;
          }

          const result = await validateExcelA1(excelFile);

          if (!result.valid) {
            notifications.show({
              title: "Error",
              message: result.message,
              color: "red",
            });
            return;
          }

          excelTitle = (await getExcelCode(excelFile)).replace(/[/\\]/g, "_");

          if (!excelTitle) {
            notifications.show({
              title: "Error",
              message: "El codi del Excel (cel·la A3) és obligatori.",
              color: "red",
            });
            return;
          }
        }

        const docsWithoutContent = docs.map(({ content, ...doc }) => doc);

        const dataForm = {
          typeId: Number(tramitsProcesValue),
          relatedTramits: tramitsProcesAnteriorValue,
          relatedTramitsLabel: selectedRelatedTramit?.label ?? "",
          formJson: "",
          otherReceivers,
          comments: getComentarisValue,
          docs: docsWithoutContent,
          docsToDelete: deletedFiles,
          isDraft,
          entitatId: props.entity.lookupId,
          ...(tipoReferencia === "filial" && {
            filialId: Number(tramitsFilialValue),
          }),
          ...(excelTitle !== undefined && { title: excelTitle }),
        };

        if (editar && location.state?.tramitType) {
          const result = await props.apiService.CreateTramit(dataForm);

          if (result.statusText === "OK") {
            notifications.show({
              title: "Tràmit actualitzat",
              message: "El tràmit s'ha actualitzat correctament",
              color: "teal",
            });

            setLoadResponse(true);
            setCodi(result.data.itemGuid);
            setTramitId(result.data.itemId);

            if (!isDraft) {
              setPDF(result.data.document.fileContents);
            }
          } else {
            notifications.show({
              title: "Error",
              message: result.message || "S'ha produït un error inesperat.",
              color: "red",
            });
          }
        } else {
          // console.log(dataForm)
          // return;
          const result = await props.apiService.CreateTramit(dataForm);

          if (result.statusText === "OK") {
            //  const resultDocsList = [];

            // for (const doc of docs) {
            //   const docsForm = {
            //     itemId: result.data.itemId,
            //     listName: "Tramits",
            //     docs: [doc],
            //   };

            //   const resultDocs = await props.apiService.UploadDocumentOV(docsForm);
            //   resultDocsList.push(resultDocs);
            // }

            const { itemId } = result.data;

            if (uploadedFiles.length > 0) {
              setUploadProgress({ done: 0, total: uploadedFiles.length });
              try {
                const CONCURRENCY = 5;
                for (let i = 0; i < uploadedFiles.length; i += CONCURRENCY) {
                  const batch = uploadedFiles.slice(i, i + CONCURRENCY);
                  await Promise.all(
                    batch.map((item) =>
                      uploadFileInChunks(item.file, itemId, "Tramits", props.apiService).then(() => {
                        setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : prev);
                      }),
                    ),
                  );
                }
              } finally {
                setUploadProgress(null);
              }
            }

            notifications.show({
              title: isDraft ? "Esborrany desat" : "Tràmit enviat",
              message: isDraft
                ? "L'esborrany s'ha desat correctament"
                : "El tràmit s'ha enviat correctament",
              color: "teal",
            });

            setLoadResponse(true);
            setCodi(result.data.itemGuid);
            setTramitId(result.data.itemId);

            if (!isDraft) {
              setPDF(result.data.document.fileContents);
            }
          } else {
            notifications.show({
              title: "Error",
              message: result.message || "S'ha produït un error inesperat.",
              color: "red",
            });
          }
        }
      } catch (error: any) {
        console.error("Error enviando tràmit:", error);
        notifications.show({
          title: "Error",
          message:
            error.message || "S'ha produït un error en enviar el tràmit.",
          color: "red",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <PageWithBreadcrumbs onBreadcrumbNavigate={handleBreadcrumbNavigation}>
      {loadResponsePDF ? (
        <>
          <Title order={1}>Acusament de recepció</Title>
          <ContainerWhite>
            <Box bg="brand.0" p="md" style={{ borderRadius: "8px" }}>
              <Group
                gap="xl"
                align="flex-start"
                justify="space-between"
                style={{ width: "100%" }}
              >
                <div style={{ flex: 1 }}>
                  {pdf ? (
                    <AfaFieldDisplay label="El tràmit s'ha realizat correctament" />
                  ) : (
                    <AfaFieldDisplay label="L'esborrany s'ha desat correctament" />
                  )}

                  <Text size="md" fw={400}>
                    Codi de seguiment: {codi}
                  </Text>
                </div>
              </Group>
              <div style={{ marginTop: "20px" }}>
                {pdf && (
                  <iframe
                    src={`data:application/pdf;base64,${pdf}`}
                    width="100%"
                    height="600"
                    title="Acusament de recepció"
                  />
                )}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <AfaButton
                  id="btn-tornar"
                  variant="outline"
                  onClick={() => navigate("/tramits/consultar")}
                >
                  Tornar
                </AfaButton>
                <AfaButton
                  id="btn-detall"
                  onClick={() => navigate("/tramits/detall/" + tramitId)}
                >
                  Anar al detall
                </AfaButton>
              </div>
            </Box>
          </ContainerWhite>
        </>
      ) : (
        <>
          <Title order={1}>{titleInit}</Title>
          <ContainerWhite>
            <Box bg="brand.0" p="md" style={{ borderRadius: "8px" }}>
              <Group gap="xl" align="flex-start">
                <AfaFieldDisplay
                  label="Entitat"
                  value={props.entity != null ? props.entity.lookupValue : ""}
                  style={{ flex: 1 }}
                />
                <AfaFieldDisplay
                  label="Informació d'interès"
                  button={{
                    text: "Consultar informació",
                    onClick: () => setModalOpened(true),
                    id: "btn-consultar-info",
                  }}
                  style={{ flex: 1 }}
                />
              </Group>
            </Box>

            {loadingPage ? (
              <AfaPageLoader message="Carregant formulari..." />
            ) : (
              <>
                <Box mt={"md"}>
                  <Stack gap="md">
                    {/* LÍNEA 1: Radio buttons + Filial (condicional) */}
                    <Group align="flex-start">
                      <div style={{ flex: 1 }}>
                        <Radio.Group
                          name="tipoReferencia"
                          label="Vull iniciar un tràmit referent a una"
                          value={tipoReferencia}
                          onChange={handleTramitsReferenceChange}
                          required
                        >
                          <Group mt="xs">
                            <Radio
                              disabled={editar}
                              value="entitat"
                              label="Entitat"
                            />
                            <Radio
                              disabled={editar}
                              value="filial"
                              label="Filial"
                            />
                          </Group>
                        </Radio.Group>
                      </div>

                      <div style={{ flex: 1 }}>
                        {tipoReferencia === "filial" && (
                          <AfaCombobox
                            name="filial"
                            id="filial"
                            label="Filial"
                            placeholder="Seleccioni una filial"
                            data={tramitsFilialData}
                            value={tramitsFilialValue}
                            disabled={editar || loadingFilial}
                            loading={loadingFilial}
                            required
                            onChange={handleTramitsFilialChange}
                          />
                        )}
                      </div>
                    </Group>
                    {/* LÍNEA 2: Tipus de procés + Procés */}
                    <Group align="flex-start">
                      <div style={{ flex: 1 }}>
                        <AfaCombobox
                          name="tipusProces"
                          id="tipus-proces"
                          label="Tipus de procés"
                          placeholder="Seleccioni una opció"
                          data={tramitsTipusProcesData}
                          value={tramitsTipusProcesValue}
                          onChange={handleTramitsTipusProcesChange}
                          disabled={
                            !tipusProcesEnable || loadingTypes || editar
                          }
                          loading={tipusProcesEnable && loadingTypes}
                          required
                        />
                      </div>

                      <div style={{ flex: 1 }}>
                        <AfaCombobox
                          name="proces"
                          id="proces"
                          label="Procés"
                          placeholder="Seleccioni una opció"
                          data={tramitsProcesData.sort((a, b) =>
                            a.label.localeCompare(b.label),
                          )}
                          value={tramitsProcesValue}
                          disabled={!procesEnable}
                          onChange={handleTramitsProcesChange}
                          required
                        />
                      </div>
                    </Group>

                    {procesAnteriorEnable && (
                      <Group
                        align="flex-start"
                        style={{ width: "50%", alignItems: "flex-end" }}
                      >
                        <div style={{ flex: 1 }}>
                          <AfaCombobox
                            name="procesAnt"
                            id="procesAnt"
                            label="En cas que el tràmit estigui vinculat a un tràmit anterior, indiqueu-ne l'identificador"
                            placeholder="Seleccioni una opció"
                            data={tramitsProcesAnteriorData}
                            value={tramitsProcesAnteriorValue}
                            onChange={handleTramitsProcesAnteriorChange}
                          />
                        </div>
                        <a rel="stylesheet" href="/tramits/consultar">
                          O bé, continuar des del llistat
                        </a>
                        {/* <link rel="stylesheet" title="O bé, continuar des del llistat" href="O bé, continuar des del llistat" /> */}
                      </Group>
                    )}

                    {/* LÍNEA 3: Comentaris + Destinataris */}
                    <Group align="flex-start">
                      <div style={{ flex: 1 }}>
                        <AfaTextarea
                          id="comentaris"
                          name="comentaris"
                          label="Comentaris"
                          placeholder="Escriure aquí"
                          autosize
                          minRows={3}
                          maxRows={6}
                          value={getComentarisValue}
                          onChange={(e) => setComentarisValue(e.target.value)}
                        />
                      </div>

                      <div style={{ flex: 1 }}>
                        <DestinatarisEditable
                          ref={destinatarisRef}
                          value={otherReceivers}
                          onChange={setOtherReceivers}
                        />
                      </div>
                    </Group>

                    <form ref={formRef}>
                      {groups.length > 0 && (
                        <>
                          <Title order={3}>Formulari</Title>
                          <hr />
                        </>
                      )}
                      <Group align="flex-start">
                        <div style={{ flex: 1 }}>
                          {groups.map((group, groupIndex) => (
                            <div
                              key={groupIndex}
                              style={{ marginBottom: "24px" }}
                            >
                              <Title order={3}>{group.groupName}</Title>
                              <Box mt="xs">
                                <Stack gap="xs">
                                  <Grid gutter="md">
                                    {group.fields.map((field, index) => {
                                      let span = 12;
                                      if (field.colsNumber === 2) span = 6;
                                      if (field.colsNumber === 3) span = 4;

                                      return (
                                        <Grid.Col key={index} span={span}>
                                          {field.fieldType === "Text" && (
                                            <AfaTextInput
                                              id={`text_${groupIndex}_${index}`}
                                              name={field.title}
                                              placeholder="Insert"
                                              label={field.title}
                                              type="text"
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType === "Select" && (
                                            <AfaSelect
                                              id={`select_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              placeholder="Seleccioni"
                                              data={field.options ?? []}
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType ===
                                            "Multiple Text" && (
                                            <AfaTextarea
                                              id={`multi_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              placeholder="Escriure aquí"
                                              autosize
                                              minRows={3}
                                              maxRows={6}
                                              required={field.required}
                                              defaultValue={field.value || ""}
                                            />
                                          )}

                                          {field.fieldType === "DateTime" && (
                                            <AfaDatePickerInput
                                              id={`date_${groupIndex}_${index}`}
                                              name={field.title}
                                              label={field.title}
                                              required={field.required}
                                              defaultValue={
                                                field.value
                                                  ? new Date(field.value)
                                                  : null
                                              }
                                            />
                                          )}
                                        </Grid.Col>
                                      );
                                    })}
                                  </Grid>
                                </Stack>
                              </Box>
                            </div>
                          ))}
                        </div>
                      </Group>
                    </form>
                  </Stack>
                </Box>

                {/* Document upload section */}
                <Title order={3} mt={"xl"}>
                  Documents
                </Title>

                <Box mt="xs">
                  <AfaDropzone
                    onDrop={async (files) => {
                      const newFiles: Array<{
                        file: File;
                        name: string;
                        type:
                          | "pdf"
                          | "doc"
                          | "xls"
                          | "ppt"
                          | "png"
                          | "jpg"
                          | "other";
                      }> = [];

                      for (const file of files) {
                        const extension = getFileExtension(file.name);
                        let type:
                          | "pdf"
                          | "doc"
                          | "xls"
                          | "ppt"
                          | "png"
                          | "jpg"
                          | "other" = "other";

                        if (extension === "pdf") type = "pdf";
                        else if (extension === "doc" || extension === "docx")
                          type = "doc";
                        else if (extension === "xls" || extension === "xlsx")
                          type = "xls";
                        else if (extension === "ppt" || extension === "pptx")
                          type = "ppt";
                        else if (extension === "png") type = "png";
                        else if (extension === "jpg" || extension === "jpeg")
                          type = "jpg";

                        if (type === "xls") {
                          const hasData = await validateExcelHasData(file);

                          if (!hasData) {
                            notifications.show({
                              title: "Error",
                              message: "Aqueste archiu esta buit",
                              color: "red",
                            });
                            continue;
                          }
                        }

                        if (type === "doc") {
                          if (extension === "doc") {
                            notifications.show({
                              title: "Error",
                              message: "Aqueste archiu esta buit",
                              color: "red",
                            });
                            continue;
                          }

                          const hasText = await validateWordHasText(file);

                          if (!hasText) {
                            notifications.show({
                              title: "Error",
                              message: "Error: Aquest arxiu Word està buit",
                              color: "orange",
                            });
                            continue;
                          }
                        }

                        newFiles.push({
                          file,
                          name: file.name,
                          type,
                        });
                      }

                      if (newFiles.length > 0) {
                        setUploadedFiles((prevFiles) => [
                          ...prevFiles,
                          ...newFiles,
                        ]);
                      }
                    }}
                    uploadedFiles={uploadedFiles}
                    onDeleteFile={(_, index) => {
                      const newFiles = uploadedFiles.filter(
                        (_, i) => i !== index,
                      );
                      setUploadedFiles(newFiles);

                      const fileToDelete = uploadedFiles[index];
                      if (!fileToDelete) return;

                      setDeletedFiles((prev) => [...prev, fileToDelete.name]);
                    }}
                  />
                </Box>
              </>
            )}

            <Box mt="xl">
              <AfaCheckbox
                onChange={(event) => setCheckTerms(event.currentTarget.checked)}
                label={
                  <>
                    Accepto els termes establerts en l'
                    <AfaAnchor
                      target="_blank"
                      href="https://www.afa.ad/ca/avis-legal"
                      size="md"
                    >
                      Avís legal
                    </AfaAnchor>{" "}
                    i la{" "}
                    <AfaAnchor
                      target="_blank"
                      href="https://www.afa.ad/ca/politica-de-privacitat"
                      size="md"
                    >
                      Política de privacitat
                    </AfaAnchor>{" "}
                    <span style={{ color: "var(--mantine-color-red-6)" }}>
                      *
                    </span>
                  </>
                }
              />
            </Box>
          </ContainerWhite>

          {/* Botones de acción */}
          <Group justify="space-between" mt="xs">
            <AfaButton
              id="btn-cancelar"
              variant="outline"
              onClick={() => {
                setPendingNavigation(null); // Limpiar cualquier navegación pendiente
                setCancelModalOpened(true);
              }}
            >
              Cancel·lar
            </AfaButton>
            <Group gap="md">
              <AfaButton
                id="btn-desa-esborrany"
                variant="outline"
                onClick={() => handleEnviarTramesaNew(true)}
                disabled={isSubmitting}
                loading={isSubmitting}
                loaderProps={{ type: "dots" }}
              >
                {isSubmitting ? "Desant..." : "Desa com a esborrany"}
              </AfaButton>

              <AfaButton
                id="btn-enviar-ara"
                variant="filled"
                onClick={() => handleEnviarTramesaNew()}
                disabled={isSubmitting}
                loading={isSubmitting}
                loaderProps={{ type: "dots" }}
              >
                {isSubmitting ? "Enviant..." : "Enviar ara"}
              </AfaButton>
            </Group>
          </Group>

          <AfaModal
            opened={uploadProgress !== null}
            onClose={() => {}}
            title="Pujant documents..."
            size="sm"
            centered
            withCloseButton={false}
            closeOnClickOutside={false}
            closeOnEscape={false}
          >
            <Stack gap="md" py="sm">
              <Text size="sm" c="dimmed" ta="center">
                {uploadProgress
                  ? `Document ${uploadProgress.done} de ${uploadProgress.total} pujat correctament`
                  : "Preparant la pujada..."}
              </Text>
              <Progress
                value={uploadProgress ? (uploadProgress.done / uploadProgress.total) * 100 : 0}
                animated
                color="teal"
                size="lg"
              />
              <Text size="xs" c="dimmed" ta="center">
                Si us plau, no tanqueu aquesta pàgina.
              </Text>
            </Stack>
          </AfaModal>

          {/* Modal de información */}
          <AfaModal
            opened={modalOpened}
            onClose={() => setModalOpened(false)}
            title="Informació d'interès"
            size="lg"
            centered
          >
            <Stack gap="md" mt="md">
              {infoInteres.map((item) => {
                return (
                  <AfaAnchor target="_blank" href={item.link} size="md">
                    {item.title}
                  </AfaAnchor>
                );
              })}
              <Stack>
                <Text size="md" fw={400}>
                  Per a qualsevol incidència tècnica o relacionada amb els
                  processos, si us plau, contacteu a{" "}
                  <AfaAnchor href="mailto:reporting@afa.ad" size="md">
                    reporting@afa.ad
                  </AfaAnchor>
                </Text>
              </Stack>
            </Stack>
          </AfaModal>

          {/* Modal de cancelación */}
          <AfaModal
            opened={cancelModalOpened}
            onClose={() => {
              setCancelModalOpened(false);
              setPendingNavigation(null);
            }}
            title="Cancel·lar tràmit"
            size="md"
            centered
          >
            <Stack mt={"sm"}>
              <div style={{ width: "100%" }}>
                <AfaAlert
                  type="warning"
                  variant="light"
                  title="Cancel·lar tràmit"
                >
                  Si cancel·les aquest tràmit, es perdran totes les dades
                  introduïdes. Aquesta acció no es pot desfer.
                </AfaAlert>
              </div>
            </Stack>

            <Group justify="flex-end" mt="md">
              <AfaButton
                id="btn-cancelar-tràmit"
                variant="filled"
                color="red"
                onClick={() => {
                  setCancelModalOpened(false);

                  // Si hay una navegación pendiente, navegar a ella; si no, a la página por defecto
                  if (pendingNavigation) {
                    window.location.href = pendingNavigation;
                    setPendingNavigation(null);
                  } else {
                    navigate("/tramits/consultar");
                  }
                }}
              >
                Cancel·lar tràmit
              </AfaButton>
            </Group>
          </AfaModal>
          <AfaModal
            opened={unsavedModalOpened}
            onClose={() => setUnsavedModalOpened(false)}
            title="Destinatari sense afegir"
            size="md"
            centered
          >
            <AfaAlert type="warning" variant="light" title="Atenció">
              Tens un destinatari sense afegir: <strong>{unsavedEmail}</strong>
              <br />
              Si us plau, afegeix-lo o esborra'l abans d'enviar el tràmit.
            </AfaAlert>
            <Group justify="flex-end" mt="md">
              <AfaButton onClick={() => setUnsavedModalOpened(false)}>
                D'acord
              </AfaButton>
            </Group>
          </AfaModal>
        </>
      )}
    </PageWithBreadcrumbs>
  );
}
